// Dividend analytics
export function calcYieldOnCost(dividends, cost) {
  if (!cost || cost <= 0) return 0;
  const annualDividends = Array.isArray(dividends) ? 
    dividends.reduce((sum, div) => sum + div.amount, 0) : dividends;
  return (annualDividends / cost) * 100;
}

export function projectDividends(holdings, months = 12) {
  if (!holdings || holdings.length === 0) return [];
  
  const monthlyProjection = [];
  const currentDate = new Date();
  
  for (let i = 0; i < months; i++) {
    const projectionDate = new Date(currentDate);
    projectionDate.setMonth(currentDate.getMonth() + i);
    
    const monthlyDividends = holdings.reduce((sum, holding) => {
      const monthlyDiv = (holding.annualDividend || 0) / 12;
      return sum + (holding.quantity * monthlyDiv);
    }, 0);
    
    const cumulativeDividends = monthlyProjection.reduce((sum, month) => sum + month.dividends, 0) + monthlyDividends;
    
    monthlyProjection.push({
      month: projectionDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      dividends: monthlyDividends,
      cumulative: cumulativeDividends
    });
  }
  
  return monthlyProjection;
}

export function calculateDividendCoverage(symbol, earnings, dividendPayout) {
  if (!earnings || !dividendPayout || dividendPayout <= 0) return null;
  
  return {
    symbol,
    coverageRatio: earnings / dividendPayout,
    payoutRatio: (dividendPayout / earnings) * 100,
    sustainability: earnings / dividendPayout > 1.5 ? 'Strong' : 
                   earnings / dividendPayout > 1.2 ? 'Adequate' : 'At Risk'
  };
}

export function calculateMarginPayoffTime(dividendIncome, marginCost, interestRate = 0.05) {
  if (!dividendIncome || dividendIncome <= 0 || !marginCost || marginCost <= 0) {
    return { months: Infinity, feasible: false };
  }
  
  const monthlyDividends = dividendIncome / 12;
  const monthlyInterest = (marginCost * interestRate) / 12;
  
  if (monthlyDividends <= monthlyInterest) {
    return { months: Infinity, feasible: false, monthlyShortfall: monthlyInterest - monthlyDividends };
  }
  
  const netMonthlyCashFlow = monthlyDividends - monthlyInterest;
  const months = marginCost / netMonthlyCashFlow;
  
  return {
    months: Math.ceil(months),
    feasible: true,
    monthlyNetIncome: netMonthlyCashFlow,
    totalInterestPaid: monthlyInterest * months
  };
}

export function analyzeDividendTrend(historicalDividends) {
  if (!historicalDividends || historicalDividends.length < 2) {
    return { trend: 'insufficient_data', growthRate: 0 };
  }
  
  const sortedDividends = [...historicalDividends].sort((a, b) => new Date(a.date) - new Date(b.date));
  const recent = sortedDividends[sortedDividends.length - 1];
  const previous = sortedDividends[sortedDividends.length - 2];
  
  const growthRate = ((recent.amount - previous.amount) / previous.amount) * 100;
  
  let trend = 'stable';
  if (growthRate > 5) trend = 'growing';
  else if (growthRate < -5) trend = 'declining';
  
  return {
    trend,
    growthRate,
    consistency: calculateDividendConsistency(sortedDividends)
  };
}

function calculateDividendConsistency(dividends) {
  if (dividends.length < 3) return 'insufficient_data';
  
  const amounts = dividends.map(d => d.amount);
  const mean = amounts.reduce((sum, amt) => sum + amt, 0) / amounts.length;
  const variance = amounts.reduce((sum, amt) => sum + Math.pow(amt - mean, 2), 0) / amounts.length;
  const coefficientOfVariation = Math.sqrt(variance) / mean;
  
  if (coefficientOfVariation < 0.1) return 'very_consistent';
  if (coefficientOfVariation < 0.2) return 'consistent';
  if (coefficientOfVariation < 0.3) return 'moderate';
  return 'volatile';
}